import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from "@ant-design/icons";
import React from "react";

interface EhrTransferStatusProps {
  status: string;
  // A later lifecycle card for the same note exists (e.g. "completed" landed
  // after this "started"). The card stays in the thread as history — greyed,
  // with no live spinner promising work that already finished.
  frozen?: boolean;
}

const statusConfig: Record<
  string,
  { icon: React.ReactNode; label: string; sublabel: string; color: string; bg: string; border: string }
> = {
  started: {
    icon: <LoadingOutlined style={{ color: "#6366F1", fontSize: 18 }} spin />,
    label: "Transferring to EHR",
    sublabel: "Your note is being uploaded. This may take a moment...",
    color: "#6366F1",
    bg: "#EEF2FF",
    border: "#C7D2FE",
  },
  completed: {
    icon: <CheckCircleFilled style={{ color: "#10B981", fontSize: 18 }} />,
    label: "Transfer Complete",
    sublabel: "Your note was successfully transferred to your EHR.",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#A7F3D0",
  },
  failed: {
    icon: <CloseCircleFilled style={{ color: "#EF4444", fontSize: 18 }} />,
    label: "Transfer Failed",
    sublabel: "There was an issue. Check MRN/Patient ID and DOB, then try again.",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
  },
};

const EhrTransferStatus: React.FC<EhrTransferStatusProps> = ({ status, frozen }) => {
  const config = statusConfig[status];
  if (!config) return null;

  return (
    <div
      style={{
        ...styles.container,
        backgroundColor: frozen ? "#F9FAFB" : config.bg,
        borderColor: frozen ? "#E5E7EB" : config.border,
      }}
    >
      <div style={styles.row}>
        {frozen ? <span style={styles.frozenDot} /> : config.icon}
        <div style={styles.textCol}>
          <span style={{ ...styles.label, color: frozen ? "#6B7280" : config.color }}>{config.label}</span>
          <span style={styles.sublabel}>{config.sublabel}</span>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    borderRadius: "12px",
    border: "1px solid",
    padding: "12px 14px",
    marginTop: "4px",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  frozenDot: {
    width: "8px",
    height: "8px",
    borderRadius: "999px",
    backgroundColor: "#D1D5DB",
    flexShrink: 0,
    marginTop: "6px",
  },
  textCol: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  label: {
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: "1.3",
  },
  sublabel: {
    fontSize: "12px",
    color: "#6B7280",
    lineHeight: "1.4",
  },
};

export default EhrTransferStatus;
